import type {
  IDialogDismissDetail,
  TDialogFormData,
} from "../components/dialog/dialog";
import type { TThemeColors } from "../interface";

export interface FxDialogElement extends HTMLElement {
  color?: TThemeColors;
  disabled: boolean;
  backdrop: boolean;
  backdropDismiss: boolean;
  keyboardClose: boolean;
  closeButton: boolean;
  header?: string;
  subHeader?: string;
  message?: string;
  cancelText?: string;
  confirmText?: string;

  componentOnReady(): Promise<this>;
  present(): Promise<void>;
  dismiss(data?: unknown, role?: string): Promise<boolean>;
  getFormData(): Promise<TDialogFormData>;
  onDidDismiss(): Promise<IDialogDismissDetail>;
  onWillDismiss(): Promise<IDialogDismissDetail>;
}

export interface DialogOptions {
  color?: TThemeColors;
  disabled?: boolean;
  backdrop?: boolean;
  backdropDismiss?: boolean;
  keyboardClose?: boolean;
  closeButton?: boolean;
  header?: string;
  subHeader?: string;
  message?: string;
  cancelText?: string;
  confirmText?: string;
}

const TAG_NAME = "fx-dialog";

function applyOptions(dialog: FxDialogElement, options: DialogOptions): void {
  if (options.color !== undefined) dialog.color = options.color;
  if (options.disabled !== undefined) dialog.disabled = options.disabled;
  if (options.backdrop !== undefined) dialog.backdrop = options.backdrop;
  if (options.backdropDismiss !== undefined) dialog.backdropDismiss = options.backdropDismiss;
  if (options.keyboardClose !== undefined) dialog.keyboardClose = options.keyboardClose;
  if (options.closeButton !== undefined) dialog.closeButton = options.closeButton;
  if (options.header !== undefined) dialog.header = options.header;
  if (options.subHeader !== undefined) dialog.subHeader = options.subHeader;
  if (options.message !== undefined) dialog.message = options.message;
  if (options.cancelText !== undefined) dialog.cancelText = options.cancelText;
  if (options.confirmText !== undefined) dialog.confirmText = options.confirmText;
}

async function buildFxDialog(
  options: DialogOptions = {},
): Promise<FxDialogElement> {
  if (typeof document === "undefined") {
    throw new Error("fx-dialog can only be created in a browser environment");
  }

  await customElements.whenDefined(TAG_NAME);

  const dialog = document.createElement(TAG_NAME) as FxDialogElement;
  applyOptions(dialog, options);
  return dialog;
}

/**
 * Creates and presents a dialog appended to `document.body`.
 *
 * The caller owns removal of the dialog element via `dialog.remove()`.
 */
export async function createDialog(
  options: DialogOptions = {},
): Promise<FxDialogElement> {
  const dialog = await buildFxDialog(options);
  document.body.appendChild(dialog);

  if (typeof dialog.componentOnReady === "function") {
    await dialog.componentOnReady();
  }

  await dialog.present();
  return dialog;
}

async function dismissFxDialog(
  dialog?: FxDialogElement,
): Promise<boolean> {
  if (dialog) {
    return dialog.dismiss();
  }

  const active = document.querySelector(
    `${TAG_NAME}[data-open='true']`,
  ) as FxDialogElement | null;

  if (active) {
    return active.dismiss();
  }

  return false;
}

export const dialogController = {
  create: createDialog,
  dismiss: dismissFxDialog,
};

export type { TDialogFormData, IDialogDismissDetail };
